/**
 * Sending the invite and the notices that follow it, including the second step
 * when the email already has a login and the operator is asked to link it.
 */
import { useState } from 'react';

import {
  inviteUser as inviteUserApi,
  UserMgmtError,
  type ExistingAccountDetails,
  type RoleKey as ApiRoleKey,
} from '../../../../api/users';

import {
  explainInviteError,
  inviteSuccessText,
  linkExistingAccountText,
  type InviteFormState,
} from './invite-form-model';
import type { EmailMatch } from './InviteEmailField';

export function useInviteSubmit(form: InviteFormState, match: EmailMatch, onInvited?: () => void) {
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  // Held per email, so editing the address withdraws the confirmation.
  const [pendingLink, setPendingLink] = useState<{
    email: string;
    details?: ExistingAccountDetails;
  } | null>(null);
  const confirmLinkExisting = pendingLink?.email === form.email;
  const linkNotice = confirmLinkExisting ? linkExistingAccountText(pendingLink?.details) : null;

  async function submit(): Promise<void> {
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const result = await inviteUserApi({
        email: form.email.trim(),
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        roleKeys: form.roleIds as ApiRoleKey[],
        regionIds: form.regionIds,
        ...(match.kind === 'backfill' ? { linkUserId: match.user.id } : {}),
        ...(confirmLinkExisting ? { linkExistingAccount: true } : {}),
      });
      setSuccessMessage(inviteSuccessText(result.emailSent, result.linkedExistingAccount));
      onInvited?.();
    } catch (err) {
      if (err instanceof UserMgmtError && err.code === 'EMAIL_HAS_AUTH0_ACCOUNT') {
        setPendingLink({ email: form.email, details: err.details });
        return;
      }
      setErrorMessage(explainInviteError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return { submit, submitting, errorMessage, successMessage, confirmLinkExisting, linkNotice };
}
